import { useGetPerformProgressQuery } from '@/services/organizations';
import { Box, Button, Card, Details, Spinner, useBreakpoint, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContractStatus, MilestoneRequirement, ProgramType, Quote, QuoteStatus, UserPermissions } from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import {
  useGetCurrentContractQuery,
  useSendContractV2Mutation,
  useVoidContractMutation,
} from '../../services/contract';
import { useGetQuotesQuery, useVoidQuoteMutation } from '../../services/quotes';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePermissions from '../auth/requirePermissions';
import {
  CHILD_INDENT,
  ConnectorChildArc,
  ConnectorParentLine,
  INFO_CONNECTOR_COLOR,
  SUCCESS_CONNECTOR_COLOR,
  WARNING_CONNECTOR_COLOR,
} from '../ChangeOrder/changeOrderConnector';
import { StatusIcon } from '../StatusIcon';

import { EquipmentQuoteListItem } from './EquipmentQuoteListItem';
import { QuoteListItem } from './QuoteListItem';
import QuoteModal from './QuoteModal';

export function Quotes({ className, hideHeader }: { className?: string; hideHeader?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType;
  const {
    data: quotes,
    isLoading: quotesIsLoading,
    isError: quotesIsError,
  } = useGetQuotesQuery({ id, programType }, { skip: !programType });
  const { data: currentContract } = useGetCurrentContractQuery(id, { skip: !id });
  const allowsAction = useIsOrgReadOnly();

  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { isPhone } = useBreakpoint();
  const quotesWithContract = quotes?.reduce((acc, quote) => {
    return [...acc, { ...quote, contractStatus: currentContract?.status || undefined, programType }];
  }, []);
  const [_voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const [_sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const [_voidContract, { isLoading: isVoidingContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });
  const status = useMemo(
    () =>
      account?.milestones?.reduce(
        (acc: any, milestone: any) => {
          const quote = milestone.requirements.find((req: any) => req.type === MilestoneRequirement.quoteActive);
          if (quote) {
            acc.quote = quote.status;
          }
          return acc;
        },
        {} as Record<any, any>,
      ),
    [account],
  );
  const hasActiveOrContractedQuotes = quotesWithContract?.some(
    (quote: any) => quote.status === QuoteStatus.active || quote.status === QuoteStatus.contracted,
  );
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);

  const disableActions =
    isVoidingQuote || isSendingContract || isVoidingContract || !canPerformProgressBasedOnOrgStatus || !allowsAction;

  const { easyChangeOrder = false } = useFlags();
  const hasEligibleContract =
    currentContract?.status === ContractStatus.approved || currentContract?.status === ContractStatus.signed;
  const hasPendingChangeOrder = quotes?.some((q: Quote) => q.status === QuoteStatus.changeOrderPending);
  const showCreateChangeOrderButton = easyChangeOrder && hasEligibleContract && !hasPendingChangeOrder;

  return (
    <>
      <Card className={className}>
        {!hideHeader && (
          <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
            <Box alignItems="center" direction="row">
              <Box padding="0 xs 0 0">
                <StatusIcon status={status?.quote} size="md" />
              </Box>
              <Box as="h3" fontWeight="medium" fontSize="md">
                Quotes {quotesWithContract?.length > 0 && `(${quotesWithContract?.length})`}
              </Box>
            </Box>

            <Box direction="row" childGap="xs" alignItems="center">
              {hasActiveOrContractedQuotes ? (
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    as="button"
                    onClick={() => {
                      if (allowsAction) {
                        openModal();
                      }
                    }}
                    size="sm"
                    variant="primary"
                    tone="neutral"
                    iconPrefix="add"
                    isDisabled={disableActions}
                  >
                    {!isPhone && 'Quote'}
                  </Button>
                </RequirePermissions>
              ) : (
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    as="button"
                    navigate={() =>
                      allowsAction
                        ? navigate(
                            [
                              ProgramType.hvac,
                              ProgramType.waterHeater,
                              ProgramType.generator,
                              ProgramType.geothermal,
                              ProgramType.energyBackup,
                            ].includes(programType)
                              ? `/accounts/${id}/system-design`
                              : `/accounts/${id}/quote/create`,
                          )
                        : undefined
                    }
                    size="sm"
                    variant="primary"
                    tone="neutral"
                    iconPrefix="add"
                    isDisabled={disableActions}
                  >
                    {!isPhone && 'Quote'}
                  </Button>
                </RequirePermissions>
              )}
            </Box>
          </Box>
        )}
        <QuotesList
          isLoading={quotesIsLoading}
          items={quotesWithContract}
          programType={programType}
          isError={quotesIsError}
          showCreateChangeOrderButton={showCreateChangeOrderButton}
        />
      </Card>
      <QuoteModal isModalOpen={isModalOpen} closeModal={closeModal} />
    </>
  );
}

export interface QuotesListProps {
  isLoading: boolean;
  isError: boolean;
  items: any[];
  programType: ProgramType;
  showCreateChangeOrderButton?: boolean;
}

// Connector color follows the child badge, per the status legend: yellow = voided,
// green = active/contracted, blue = changeOrderPending/amended.
export function getQuoteConnectorColor(status: QuoteStatus): string {
  if (status === QuoteStatus.voided) return WARNING_CONNECTOR_COLOR;
  if (status === QuoteStatus.active || status === QuoteStatus.contracted) return SUCCESS_CONNECTOR_COLOR;
  return INFO_CONNECTOR_COLOR;
}

function renderQuoteItems(items: Quote[], showCreateChangeOrderButton: boolean | undefined): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    const nextItem = items[i + 1];
    // Prefer the active/contracted → changeOrderPending link (the current pending CO). Only pair
    // amended → active when no pending CO follows, so a [amended, active, changeOrderPending] list
    // connects active → changeOrderPending rather than consuming amended + active and orphaning it.
    const isConnectedPair =
      ([QuoteStatus.active, QuoteStatus.contracted].includes(item.status) &&
        nextItem?.status === QuoteStatus.changeOrderPending) ||
      (item.status === QuoteStatus.amended &&
        nextItem?.status === QuoteStatus.active &&
        items[i + 2]?.status !== QuoteStatus.changeOrderPending);

    if (isConnectedPair) {
      // Connector color matches the child badge variant (see getQuoteConnectorColor).
      const connectorColor = getQuoteConnectorColor(nextItem.status);
      // Arc lands on the child badge's vertical center. CHANGE ORDER PENDING wraps to 3 lines
      // (~56px tall, center ≈ y=40); a single-line badge (ACTIVE) center ≈ y=24.
      // Row padding-top is `sm` (12px), so the parent badge bottom (line start) sits at ~34px.
      const arcHeight = nextItem.status === QuoteStatus.changeOrderPending ? 40 : 24;
      result.push(
        <Box key={item.id} style={{ position: 'relative' }}>
          <ConnectorParentLine color={connectorColor} top={34} />
          <QuoteListItem item={item} showCreateChangeOrderButton={showCreateChangeOrderButton} />
        </Box>,
        <Box key={nextItem.id} style={{ position: 'relative' }}>
          <ConnectorChildArc color={connectorColor} height={arcHeight} />
          <QuoteListItem
            item={nextItem}
            showCreateChangeOrderButton={showCreateChangeOrderButton}
            indentLeft={CHILD_INDENT}
          />
        </Box>,
      );
      i += 2;
    } else {
      result.push(
        <QuoteListItem item={item} key={item.id} showCreateChangeOrderButton={showCreateChangeOrderButton} />,
      );
      i++;
    }
  }
  return result;
}

export function QuotesList({
  isLoading,
  items = [],
  programType,
  isError,
  showCreateChangeOrderButton,
}: QuotesListProps) {
  const totalCount = items?.length || 0;
  const mainQuote = items?.[0];
  const sortedQuotes = [
    ProgramType.hvac,
    ProgramType.waterHeater,
    ProgramType.generator,
    ProgramType.geothermal,
    ProgramType.energyBackup,
  ].includes(programType)
    ? items.toSorted((a: any, b: any) => {
        const orderA = a.status === QuoteStatus.active || a.status === QuoteStatus.contracted ? 0 : 1;
        const orderB = b.status === QuoteStatus.active || b.status === QuoteStatus.contracted ? 0 : 1;
        return orderA - orderB;
      })
    : items;
  const { isOpen: isShowAllQuotesOpen, handleToggle: handleShowAllQuotesToggle } = useOpenClose();

  if (isError) {
    return (
      <Box
        display="block"
        textAlign="center"
        padding="lg"
        childGap="xl"
        borderColor="separator"
        borderWidth="xs 0 0 0"
      >
        <Box fontSize="sm" color="button-secondary-danger">
          There was an error loading quotes. Please try again or contact support.
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return <Spinner size="lg" />;
  }

  return (
    <Box width="100">
      {totalCount > 0 ? (
        <Box>
          {[
            ProgramType.hvac,
            ProgramType.waterHeater,
            ProgramType.generator,
            ProgramType.geothermal,
            ProgramType.energyBackup,
          ].includes(programType) &&
          (items?.length > 0 || sortedQuotes?.length > 0) ? (
            <>
              <EquipmentQuoteListItem item={sortedQuotes?.[0]} isMainQuote={mainQuote} />
              {sortedQuotes?.length > 1 && (
                <Details isOpen={isShowAllQuotesOpen} borderWidth="xs 0 0 0" borderColor="separator">
                  <Details.Summary
                    isDetailsOpen={isShowAllQuotesOpen}
                    onToggle={handleShowAllQuotesToggle}
                    display="flex"
                    padding="sm lg sm lg"
                  >
                    <Box textAlign="center">
                      <Box>{isShowAllQuotesOpen ? 'Hide All Quotes' : 'Show All Quotes'}</Box>
                    </Box>
                  </Details.Summary>
                  {sortedQuotes.slice(1).map((quote: any, index: number) => (
                    <Box key={`quote-${index}`} padding={'0 0 xs 0'}>
                      <EquipmentQuoteListItem item={quote} />
                    </Box>
                  ))}
                </Details>
              )}
            </>
          ) : (
            renderQuoteItems(items as Quote[], showCreateChangeOrderButton)
          )}
        </Box>
      ) : (
        <Box
          display="block"
          textAlign="center"
          padding="lg"
          childGap="xl"
          borderColor="separator"
          borderWidth="xs 0 0 0"
        >
          <Box fontSize="sm" color="body-secondary">
            No quotes exist for this account
          </Box>
        </Box>
      )}
    </Box>
  );
}
